import { useEffect, useCallback } from 'react';
import { NODE_ENV } from '../../constants';

const isEnvDevelopment = NODE_ENV === 'development';

// TODO: Later when we know the exact origins, we should consider adding a fixed origin check here in the future for better security,
// instead of using '*'. This would involve specifying the exact origin(s) that are allowed to receive messages,
// which can help prevent potential security risks associated with sending messages to unintended recipients.
// Example:
// export const AUTHORIZED_EVENT_ORIGINS = ['link-1', 'link-2'];
// const isAuthorizedOrigin = (origin: string) => AUTHORIZED_EVENT_ORIGINS.includes(origin);

const TARGET_ORIGIN = '*';

type Message = {
  type: string;
  data?: unknown;
};

type UsePostMessageChannelParams = {
  onMessage?: (data: { message: Message; event: MessageEvent }) => void;
};

export function usePostMessageChannel({
  onMessage,
}: UsePostMessageChannelParams = {}) {
  const sendMessageToParentWindow = useCallback(
    (type: string, data?: unknown) => {
      if (
        window.parent &&
        window !== window.parent &&
        typeof window.parent.postMessage === 'function'
      ) {
        const message = { type, data };
        window.parent.postMessage(message, TARGET_ORIGIN);
      } else {
        if (isEnvDevelopment) {
          console.log(
            '[Simulation Only] Sending message event to Parent Window: ',
            'event-type: ',
            type,
            'event-details: ',
            data
          );
        }
      }
    },
    []
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const message = event.data;

      if (
        !message ||
        typeof message !== 'object' ||
        typeof message.type !== 'string'
      ) {
        return;
      }

      onMessage?.({ message, event });
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onMessage]);

  return { sendMessageToParentWindow };
}
